import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { type TTicketBySpace } from '../types/ticket.type';

export type TGetListArchiveTicketBySpaceResponse = ApiResponse<TTicketBySpace[]>;
export type TGetListArchiveTicketBySpaceParams = {
  spaceId: string;
};

export const getListArchiveTicketBySpace = async (
  params: TGetListArchiveTicketBySpaceParams
): Promise<TGetListArchiveTicketBySpaceResponse> => {
  const url = `/ticket/by-space-archived/${params.spaceId}`;

  return axiosInstance.get(url, { params });
};

export const GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY = 'get-list-ticketing-archive-ticket-by-space-id';

export const getListArchiveTicketBySpaceQueryOptions = <TData = TGetListArchiveTicketBySpaceResponse>(
  params: TGetListArchiveTicketBySpaceParams,
  select?: (data: TGetListArchiveTicketBySpaceResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId: params?.spaceId }],
    queryFn: () => getListArchiveTicketBySpace(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListArchiveTicketBySpaceQuery<TData>(options: {
  params: TGetListArchiveTicketBySpaceParams;
  queryConfig?: QueryConfig<typeof getListArchiveTicketBySpaceQueryOptions<TData>>;
  select: (data: TGetListArchiveTicketBySpaceResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListArchiveTicketBySpaceQuery(options: {
  params: TGetListArchiveTicketBySpaceParams;
  queryConfig?: QueryConfig<typeof getListArchiveTicketBySpaceQueryOptions<TGetListArchiveTicketBySpaceResponse>>;
}): ReturnType<typeof useQuery<TGetListArchiveTicketBySpaceResponse>>;

// Implementation
export function useListArchiveTicketBySpaceQuery<TData = TGetListArchiveTicketBySpaceResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListArchiveTicketBySpaceParams;
  queryConfig?: QueryConfig<
    typeof getListArchiveTicketBySpaceQueryOptions<TData | TGetListArchiveTicketBySpaceResponse>
  >;
  select?: (data: TGetListArchiveTicketBySpaceResponse) => TData;
}) {
  return useQuery({
    ...getListArchiveTicketBySpaceQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListArchiveTicketBySpaceResponse ? TGetListArchiveTicketBySpaceResponse : TData>
  >;
}
